import { MOTION_WORD } from "@/lib/generation-motion/plan"

/** Альфа-карта буквы с мягким краем: по ней точки сетки решают, светиться ли и насколько. */
export type MotionGlyph = {
  ch: string
  width: number
  height: number
  fontPx: number
  /** Центр видимой части буквы в пикселях карты. */
  cx: number
  cy: number
  /** Видимая ширина и половина высоты в долях кегля. */
  widthRatio: number
  capHalf: number
  alpha: Uint8ClampedArray
}

const FONT_PX = 160
const FONT_FAMILY = "Gabarito, ui-rounded, 'SF Pro Rounded', 'Arial Rounded MT Bold', system-ui, sans-serif"

function boxBlur(src: Uint8ClampedArray, w: number, h: number, radius: number) {
  const tmp = new Float32Array(w * h)
  const out = new Uint8ClampedArray(w * h)
  const span = radius * 2 + 1
  for (let y = 0; y < h; y++) {
    let acc = 0
    for (let x = -radius; x <= radius; x++) acc += src[y * w + Math.min(w - 1, Math.max(0, x))]
    for (let x = 0; x < w; x++) {
      tmp[y * w + x] = acc / span
      const add = Math.min(w - 1, x + radius + 1)
      const sub = Math.max(0, x - radius)
      acc += src[y * w + add] - src[y * w + sub]
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0
    for (let y = -radius; y <= radius; y++) acc += tmp[Math.min(h - 1, Math.max(0, y)) * w + x]
    for (let y = 0; y < h; y++) {
      out[y * w + x] = acc / span
      const add = Math.min(h - 1, y + radius + 1)
      const sub = Math.max(0, y - radius)
      acc += tmp[add * w + x] - tmp[sub * w + x]
    }
  }
  return out
}

function rasterize(ctx: CanvasRenderingContext2D, ch: string): MotionGlyph {
  const size = Math.round(FONT_PX * 1.3)
  ctx.clearRect(0, 0, size, size)
  ctx.fillStyle = "#fff"
  ctx.font = `800 ${FONT_PX}px ${FONT_FAMILY}`
  ctx.textAlign = "center"
  ctx.textBaseline = "alphabetic"
  ctx.fillText(ch, size / 2, size / 2 + FONT_PX * 0.36)
  const rgba = ctx.getImageData(0, 0, size, size).data
  const raw = new Uint8ClampedArray(size * size)
  let minX = size
  let maxX = 0
  let minY = size
  let maxY = 0
  for (let i = 0; i < size * size; i++) {
    const a = rgba[i * 4 + 3]
    raw[i] = a
    if (a > 127) {
      const x = i % size
      const y = (i - x) / size
      minX = Math.min(minX, x)
      maxX = Math.max(maxX, x)
      minY = Math.min(minY, y)
      maxY = Math.max(maxY, y)
    }
  }
  if (maxX < minX) {
    minX = maxX = size / 2
    minY = maxY = size / 2
  }
  const radius = Math.max(1, Math.round(FONT_PX * 0.02))
  const alpha = boxBlur(boxBlur(raw, size, size, radius), size, size, radius)
  return {
    ch,
    width: size,
    height: size,
    fontPx: FONT_PX,
    cx: (minX + maxX + 1) / 2,
    cy: (minY + maxY + 1) / 2,
    widthRatio: (maxX - minX + 1) / FONT_PX,
    capHalf: (maxY - minY + 1) / 2 / FONT_PX,
    alpha,
  }
}

let glyphsPromise: Promise<MotionGlyph[]> | null = null

/** Буквы слова, растеризованные фирменным шрифтом. Считаются один раз на вкладку. */
export function loadMotionGlyphs(): Promise<MotionGlyph[]> {
  if (glyphsPromise) return glyphsPromise
  glyphsPromise = (async () => {
    try {
      await document.fonts.load(`800 ${FONT_PX}px Gabarito`, MOTION_WORD)
    } catch {
      // Шрифт не загрузился — растеризуем запасным скруглённым.
    }
    const canvas = document.createElement("canvas")
    canvas.width = canvas.height = Math.round(FONT_PX * 1.3)
    const ctx = canvas.getContext("2d", { willReadFrequently: true })
    if (!ctx) return []
    return [...new Set(MOTION_WORD)].map((ch) => rasterize(ctx, ch))
  })()
  return glyphsPromise
}
